'use client';

import Link from 'next/link';
import { useEffect, useState } from 'react';

import { getLayoutStore } from '@wwe-portal/web/providers/stores/layout-store';
import { isOddsPageAvailable } from '@wwe-portal/web/services/client/item-probabilities';

/**
 * The odds-disclosure link, shown only to the readers the page exists for.
 *
 * `/item-probabilities` satisfies Korea's odds-disclosure rules and is published to Korea
 * alone, so everywhere else the link would lead to a 404. Which country a reader is in
 * cannot be decided while rendering, though: this link sits in the sidebar, part of a
 * document that every reader shares in the CDN cache. Deciding there would serve
 * one reader's answer to all of them, unless the viewer country joined the cache key of the
 * entire site.
 *
 * So the server renders nothing, and the browser asks the odds API — behind the same guard
 * that serves the tables — once it has mounted. The cached HTML stays identical worldwide
 * and the link appears for Korea a moment later. On dev and qa the guard admits everyone,
 * so the link behaves there exactly as it always has, with no environment check here.
 *
 * The label itself stays English, like every other string in this nav. The page behind it
 * is Korean, but the chrome around it is not, and one Korean word among English neighbours
 * read as an inconsistency rather than as a courtesy.
 */
export const OddsLink = ({
  className,
  dataId,
}: {
  className?: string;
  dataId: string;
}) => {
  const [available, setAvailable] = useState(false);

  useEffect(() => {
    let mounted = true;

    // Never rejects: an inconclusive probe resolves false and is retried on the next mount.
    isOddsPageAvailable().then((result) => {
      if (mounted) setAvailable(result);
    });

    return () => {
      mounted = false;
    };
  }, []);

  if (!available) return null;

  return (
    <Link
      className={className}
      data-id={dataId}
      href="/item-probabilities"
      // The drawer stays mounted across a client-side navigation, so it has to be closed
      // explicitly — exactly what LeftSidebarMenuItem does for News, Leaderboard and the
      // rest. The neighbours in this row never needed it: Terms and Privacy open in a new
      // tab and Cookies opens a banner, which makes this the only internal link among them.
      onClick={() => getLayoutStore().getState().setSidebar('none')}
    >
      Odds
    </Link>
  );
};
